import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';
import {
  Mic, Square, Trash2, Clock, FileText, Sparkles, Loader2, AlertCircle,
  Search, X, Calendar, User, Tag, ChevronDown, ChevronRight, Filter
} from 'lucide-react';

const CATEGORIES = {
  polizza: { label: 'Polizza', color: 'bg-blue-100 text-blue-700' },
  preventivo: { label: 'Preventivo', color: 'bg-amber-100 text-amber-700' },
  sinistro: { label: 'Sinistro', color: 'bg-red-100 text-red-700' },
  scadenza: { label: 'Scadenza', color: 'bg-orange-100 text-orange-700' },
  cliente: { label: 'Cliente', color: 'bg-green-100 text-green-700' },
  pagamento: { label: 'Pagamento', color: 'bg-purple-100 text-purple-700' },
  consulenza: { label: 'Consulenza', color: 'bg-teal-100 text-teal-700' },
  generico: { label: 'Generico', color: 'bg-slate-100 text-slate-600' },
};

const DATE_PRESETS = [
  { label: 'Oggi', value: 'today' },
  { label: 'Settimana', value: 'week' },
  { label: 'Mese', value: 'month' },
  { label: 'Anno', value: 'year' },
  { label: 'Tutte', value: 'all' },
];

export const NoteAI = () => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [expandedNote, setExpandedNote] = useState(null);
  const [liveText, setLiveText] = useState('');

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [datePreset, setDatePreset] = useState('all');
  const [filterPerson, setFilterPerson] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filtersData, setFiltersData] = useState({ persone: [], categorie: [] });
  const [showFilters, setShowFilters] = useState(false);

  const mediaRecorderRef = useRef(null);
  const fullChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const liveIntervalRef = useRef(null);
  const liveChunksRef = useRef([]);
  const isRecordingRef = useRef(false);

  // ─── Fetch notes with filters ───
  const fetchNotes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery.trim()) params.append('q', searchQuery.trim());
      if (filterPerson) params.append('persona', filterPerson);
      if (filterCategory) params.append('categoria', filterCategory);

      if (datePreset !== 'all') {
        const now = new Date();
        let from;
        if (datePreset === 'today') {
          from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (datePreset === 'week') {
          from = new Date(now);
          from.setDate(from.getDate() - 7);
        } else if (datePreset === 'month') {
          from = new Date(now);
          from.setMonth(from.getMonth() - 1);
        } else if (datePreset === 'year') {
          from = new Date(now);
          from.setFullYear(from.getFullYear() - 1);
        }
        if (from) params.append('data_da', from.toISOString());
      }

      const qs = params.toString();
      const res = await api.get(`/ai/notes${qs ? `?${qs}` : ''}`);
      setNotes(res.data);
    } catch {
      setError('Errore nel caricamento delle note');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, datePreset, filterPerson, filterCategory]);

  const fetchFilters = useCallback(async () => {
    try {
      const res = await api.get('/ai/notes/filters');
      setFiltersData(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);
  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (liveIntervalRef.current) clearInterval(liveIntervalRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // ─── Live transcription: send accumulated audio every ~12s ───
  const sendLiveChunk = useCallback(async () => {
    if (!isRecordingRef.current || liveChunksRef.current.length === 0) return;
    const blob = new Blob(liveChunksRef.current, { type: 'audio/webm' });
    if (blob.size < 2000) return;
    try {
      const formData = new FormData();
      formData.append('file', blob, 'chunk.webm');
      const res = await api.post('/ai/transcribe-live', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      if (res.data.text && isRecordingRef.current) {
        setLiveText(res.data.text);
      }
    } catch { /* ignore live chunk errors */ }
  }, []);

  // ─── Recording ───
  const startRecording = async () => {
    setError('');
    setLiveText('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      fullChunksRef.current = [];
      liveChunksRef.current = [];
      isRecordingRef.current = true;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          fullChunksRef.current.push(e.data);
          liveChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        isRecordingRef.current = false;
        stream.getTracks().forEach(t => t.stop());
        if (liveIntervalRef.current) { clearInterval(liveIntervalRef.current); liveIntervalRef.current = null; }
        const blob = new Blob(fullChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) {
          setError('Registrazione troppo breve. Riprova.');
          setLiveText('');
          return;
        }
        await sendFinalAudio(blob);
      };

      mediaRecorder.start(1000);
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(p => p + 1), 1000);

      // Start live transcription every 12 seconds
      liveIntervalRef.current = setInterval(() => {
        sendLiveChunk();
      }, 12000);
    } catch {
      setError('Permesso microfono negato. Controlla le impostazioni del browser.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    isRecordingRef.current = false;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (liveIntervalRef.current) { clearInterval(liveIntervalRef.current); liveIntervalRef.current = null; }
  };

  const sendFinalAudio = async (blob) => {
    setProcessing(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', blob, 'registrazione.webm');
      const res = await api.post('/ai/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setNotes(prev => [res.data, ...prev]);
      setExpandedNote(res.data.id);
      setLiveText('');
      fetchFilters();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore durante la trascrizione';
      setError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/ai/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      if (expandedNote === noteId) setExpandedNote(null);
      fetchFilters();
    } catch {
      setError("Errore durante l'eliminazione");
    }
  };

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getCatStyle = (cat) => CATEGORIES[cat] || CATEGORIES.generico;

  const hasActiveFilters = filterPerson || filterCategory || datePreset !== 'all';

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto" data-testid="note-ai-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="note-ai-title">Note AI</h1>
          <p className="text-sm text-slate-500 mt-1">Registra, trascrivi e cerca le tue note vocali intelligenti</p>
        </div>
      </div>

      {/* Recording Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5 shadow-sm" data-testid="recording-card">
        <div className="flex flex-col items-center gap-3">
          {processing ? (
            <div className="flex flex-col items-center gap-3 py-3">
              <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
                <Loader2 size={28} className="text-amber-600 animate-spin" />
              </div>
              <div className="text-center">
                <p className="font-medium text-slate-800 text-sm">Analisi AI in corso...</p>
                <p className="text-xs text-slate-500 mt-1">Trascrizione, riassunto e punti salienti</p>
              </div>
              {liveText && (
                <div className="w-full mt-2 bg-slate-50 rounded-lg p-3 border border-slate-200 max-h-32 overflow-y-auto">
                  <p className="text-xs text-slate-500 font-medium mb-1">Ultimo testo rilevato:</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{liveText}</p>
                </div>
              )}
            </div>
          ) : recording ? (
            <div className="flex flex-col items-center gap-3 py-3 w-full">
              <div className="flex items-center gap-4">
                <button
                  onClick={stopRecording}
                  data-testid="stop-recording-btn"
                  className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow-lg shadow-red-200"
                >
                  <Square size={24} className="text-white" fill="white" />
                </button>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="font-mono text-lg font-semibold text-slate-800" data-testid="recording-timer">
                      {formatTime(elapsed)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">Premi per fermare</p>
                </div>
              </div>
              {/* Live transcription preview */}
              <div className="w-full mt-2 bg-slate-50 rounded-lg p-3 border border-dashed border-slate-300 min-h-[60px] max-h-40 overflow-y-auto" data-testid="live-transcription">
                {liveText ? (
                  <>
                    <p className="text-xs text-amber-600 font-medium mb-1 flex items-center gap-1">
                      <Sparkles size={12} />Trascrizione in tempo reale
                    </p>
                    <p className="text-sm text-slate-700 leading-relaxed">{liveText}</p>
                  </>
                ) : (
                  <p className="text-xs text-slate-400 italic text-center py-2">
                    {elapsed < 12 ? 'La trascrizione apparira tra pochi secondi...' : 'In attesa del testo...'}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-3">
              <button
                onClick={startRecording}
                data-testid="start-recording-btn"
                className="w-16 h-16 rounded-full bg-amber-500 hover:bg-amber-600 flex items-center justify-center transition-all shadow-lg shadow-amber-200 hover:scale-105"
              >
                <Mic size={28} className="text-white" />
              </button>
              <p className="text-sm text-slate-600">Premi per registrare una nota vocale</p>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-3 flex items-center gap-2 bg-red-50 text-red-700 text-sm p-3 rounded-lg" data-testid="error-message">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Search & Filters Bar */}
      <div className="mb-4 space-y-3" data-testid="search-filters">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cerca per nome, parola chiave, argomento..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-8 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-400"
              data-testid="search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={14} />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg border text-sm transition-colors ${
              hasActiveFilters ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="toggle-filters-btn"
          >
            <Filter size={14} />
            <span className="hidden sm:inline">Filtri</span>
            {hasActiveFilters && <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />}
          </button>
        </div>

        {/* Date presets */}
        <div className="flex gap-1.5 flex-wrap">
          {DATE_PRESETS.map(p => (
            <button
              key={p.value}
              onClick={() => setDatePreset(p.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                datePreset === p.value
                  ? 'bg-slate-800 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`date-preset-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="flex gap-3 flex-wrap bg-white border border-slate-200 rounded-lg p-3" data-testid="expanded-filters">
            {filtersData.persone.length > 0 && (
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-medium text-slate-500 mb-1 block flex items-center gap-1">
                  <User size={12} /> Persona
                </label>
                <select
                  value={filterPerson}
                  onChange={(e) => setFilterPerson(e.target.value)}
                  className="w-full py-1.5 px-2 border border-slate-200 rounded-md text-sm bg-white"
                  data-testid="filter-person-select"
                >
                  <option value="">Tutte le persone</option>
                  {filtersData.persone.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            )}
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block flex items-center gap-1">
                <Tag size={12} /> Categoria
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full py-1.5 px-2 border border-slate-200 rounded-md text-sm bg-white"
                data-testid="filter-category-select"
              >
                <option value="">Tutte le categorie</option>
                {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            {hasActiveFilters && (
              <button
                onClick={() => { setFilterPerson(''); setFilterCategory(''); setDatePreset('all'); }}
                className="self-end text-xs text-red-500 hover:text-red-700 py-1.5"
                data-testid="clear-filters-btn"
              >
                Pulisci filtri
              </button>
            )}
          </div>
        )}
      </div>

      {/* Notes count */}
      {!loading && (
        <p className="text-xs text-slate-400 mb-3" data-testid="notes-count">
          {notes.length} {notes.length === 1 ? 'nota' : 'note'} {hasActiveFilters || searchQuery ? 'trovate' : ''}
        </p>
      )}

      {/* Notes List */}
      <div className="space-y-3" data-testid="notes-list">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-slate-400" size={24} />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-xl">
            <FileText size={36} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">
              {searchQuery || hasActiveFilters ? 'Nessuna nota trovata' : 'Nessuna nota ancora'}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              {searchQuery || hasActiveFilters ? 'Prova a modificare i filtri di ricerca' : 'Registra la tua prima nota vocale'}
            </p>
          </div>
        ) : (
          notes.map((note) => <NoteCard key={note.id} note={note} expanded={expandedNote === note.id}
            onToggle={() => setExpandedNote(expandedNote === note.id ? null : note.id)}
            onDelete={() => deleteNote(note.id)}
            onPersonClick={(p) => { setFilterPerson(p); setShowFilters(true); }}
            getCatStyle={getCatStyle} formatDate={formatDate} />)
        )}
      </div>
    </div>
  );
};

/* ═══ Note Card Component ═══ */
const NoteCard = ({ note, expanded, onToggle, onDelete, onPersonClick, getCatStyle, formatDate }) => {
  const catStyle = getCatStyle(note.categoria);
  const hasOldFormat = note.summary && !note.titolo;
  const title = note.titolo || (hasOldFormat ? note.transcription?.substring(0, 60) : 'Nota vocale');

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`note-card-${note.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 hover:bg-slate-50/70 transition-colors">
        <div onClick={onToggle} className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer" data-testid={`note-toggle-${note.id}`}>
          <div className="flex items-center gap-2 flex-shrink-0">
            {expanded ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-slate-800 truncate">{title}</p>
              {note.categoria && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${catStyle.color}`}>
                  {catStyle.label}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <div className="flex items-center gap-1">
                <Clock size={11} className="text-slate-400" />
                <span className="text-xs text-slate-400">{formatDate(note.created_at)}</span>
              </div>
              {note.persone?.length > 0 && (
                <div className="flex items-center gap-1 flex-wrap">
                  <User size={11} className="text-slate-400" />
                  {note.persone.slice(0, 3).map((p, i) => (
                    <button key={i} onClick={(e) => { e.stopPropagation(); onPersonClick(p); }}
                      className="text-xs text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded hover:bg-amber-100 transition-colors"
                      data-testid={`person-tag-${p.replace(/\s/g, '-')}`}>
                      {p}
                    </button>
                  ))}
                  {note.persone.length > 3 && <span className="text-xs text-slate-400">+{note.persone.length - 3}</span>}
                </div>
              )}
            </div>
          </div>
        </div>
        <button onClick={onDelete}
          className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0 ml-2"
          data-testid={`delete-note-${note.id}`}>
          <Trash2 size={15} />
        </button>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-4 bg-slate-50/50" data-testid={`note-content-${note.id}`}>
          {/* Summary */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-amber-600" />
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Riassunto AI</h3>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {note.riassunto || note.summary || '-'}
            </div>
          </div>

          {/* Key Points */}
          {note.punti_salienti?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Punti salienti</h3>
              <ul className="space-y-1.5">
                {note.punti_salienti.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200 text-sm text-slate-700">
                    <span className="text-amber-500 font-bold mt-0.5 flex-shrink-0">{i + 1}.</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* People */}
          {note.persone?.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Persone menzionate</h3>
              <div className="flex gap-2 flex-wrap">
                {note.persone.map((p, i) => (
                  <span key={i} className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-3 py-1.5 text-sm text-slate-700">
                    <User size={12} className="text-slate-400" /> {p}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Transcription */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <FileText size={14} className="text-slate-500" />
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Trascrizione completa</h3>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-200 text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
              {note.transcription}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
